import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Paper,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsIcon from '@mui/icons-material/Directions';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuIcon from '@mui/icons-material/Menu';
import LayersIcon from '@mui/icons-material/Layers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import LocalMap2 from './LocalMap2';

// Location data interface
interface LocationData {
  name: string;
  displayName: string;
  description: string;
  latitude: number;
  longitude: number;
  zoom: number;
  type: string;
  boundingBox: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

const CountrySelector: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(2);
  const [loading, setLoading] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const mapCenter = { lat: 20, lng: 0 }; // World view center

  // Geocoding function
  const geocodeLocation = async (locationName: string) => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationName)}&limit=5`
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        setSearchResults(data);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Geocoding error:', error);
      
      // Show user-friendly error message
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        alert('No internet connection. Please check your network.');
      } else if (error instanceof Error && error.message.includes('429')) {
        alert('Too many searches. Please wait a moment and try again.');
      } else {
        alert('Unable to search locations. Please try again later.');
      }
      
      setLoading(false);
      setSearchResults([]);
      setShowResults(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchQuery.length > 2) {
      const timer = setTimeout(() => {
        geocodeLocation(searchQuery);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleSelectLocation = (result: any) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    
    // Extract bounding box from result
    const boundingBox = result.boundingbox || [];
    const bbox = {
      north: boundingBox[1] ? parseFloat(boundingBox[1]) : lat + 0.1,
      south: boundingBox[0] ? parseFloat(boundingBox[0]) : lat - 0.1,
      east: boundingBox[3] ? parseFloat(boundingBox[3]) : lon + 0.1,
      west: boundingBox[2] ? parseFloat(boundingBox[2]) : lon - 0.1
    };
    
    let zoom = 10;
    if (result.type === 'country' || result.class === 'boundary') {
      zoom = 6;
    } else if (result.type === 'state' || result.type === 'province') {
      zoom = 8;
    } else if (result.type === 'city') {
      zoom = 12;
    } else {
      zoom = 15;
    }

    const locationName = result.display_name.split(',')[0].trim();

    const locationData: LocationData = {
      name: locationName,
      displayName: result.display_name,
      description: `${locationName} - ${result.display_name}`,
      latitude: lat,
      longitude: lon,
      zoom: zoom,
      type: result.type || result.class,
      boundingBox: bbox
    };

    setSelectedLocation(locationData);
    setZoomLevel(zoom);
    setMapKey(prev => prev + 1);
    setSearchQuery(locationName);
    setShowResults(false);

  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 1, 20));
    setMapKey(prev => prev + 1);
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 1, 1));
    setMapKey(prev => prev + 1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#fff' }}>
      {/* Header with Back Button */}
      <Box
        sx={{
          borderBottom: '1px solid #e5e7eb',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8fafc'
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#111827',
            borderRadius: 999,
            px: 2.5,
            py: 1,
            border: '1px solid #e5e7eb',
            background: '#ffffff',
            '&:hover': {
              background: '#f3f4f6'
            }
          }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h6" sx={{ color: '#1f2937', fontWeight: 700 }}>
          Interactive Map Explorer
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left Sidebar */}
      <Paper
        elevation={3}
        sx={{
          width: 380,
          height: '100vh',
          overflow: 'auto',
          zIndex: 1000,
          position: 'relative',
          borderRight: '1px solid #e0e0e0'
        }}
      >
        {/* Search Bar */}
        <Box sx={{ p: 2, position: 'relative' }}>
          <TextField
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search location (e.g., India, Paris, Tokyo)..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton size="small">
                    <MenuIcon />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <IconButton size="small" onClick={handleClearSearch}>
                      {searchQuery ? <CloseIcon /> : <SearchIcon />}
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f1f3f4'
              }
            }}
          />

          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 16,
                right: 16,
                mt: 1,
                maxHeight: 300,
                overflow: 'auto',
                zIndex: 1001,
                border: '1px solid #e0e0e0'
              }}
            >
              <List>
                {searchResults.map((result, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => handleSelectLocation(result)}>
                      <ListItemText
                        primary={result.display_name.split(',')[0]}
                        secondary={result.display_name}
                        primaryTypographyProps={{
                          fontWeight: 600
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.75rem',
                          noWrap: false
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Box>

        {/* Location Name */}
        <Box sx={{ px: 3, py: 2 }}>
          {selectedLocation ? (
            <>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                {selectedLocation.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {selectedLocation.displayName}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: '#999' }}>
                No Location Selected
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Search for any country, city, or place to get started
              </Typography>
            </>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            px: 2,
            py: 2,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Button sx={{ flexDirection: 'column', minWidth: 'auto', textTransform: 'none' }}>
            <Avatar sx={{ bgcolor: '#1a73e8', width: 40, height: 40, mb: 0.5 }}>
              <DirectionsIcon fontSize="small" />
            </Avatar>
            <Typography variant="caption">Directions</Typography>
          </Button>

          <Button sx={{ flexDirection: 'column', minWidth: 'auto', textTransform: 'none' }}>
            <Avatar sx={{ bgcolor: 'transparent', border: '1px solid #dadce0', width: 40, height: 40, mb: 0.5 }}>
              <BookmarkBorderIcon fontSize="small" sx={{ color: '#5f6368' }} />
            </Avatar>
            <Typography variant="caption">Save</Typography>
          </Button>

          <Button sx={{ flexDirection: 'column', minWidth: 'auto', textTransform: 'none' }}>
            <Avatar sx={{ bgcolor: 'transparent', border: '1px solid #dadce0', width: 40, height: 40, mb: 0.5 }}>
              <ShareIcon fontSize="small" sx={{ color: '#5f6368' }} />
            </Avatar>
            <Typography variant="caption">Share</Typography>
          </Button>
        </Box>

        {/* Quick Facts */}
        {selectedLocation && (
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Quick facts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              {selectedLocation.description}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Right Side - Mapbox Map */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          bgcolor: '#e0e0e0'
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2
            }}
          >
            <CircularProgress sx={{ color: '#7c3aed' }} />
            <Typography sx={{ color: '#374151', ml: 2 }}>Loading map...</Typography>
          </Box>
        )}

        <Box sx={{ position: 'absolute', inset: 0 }}>
          <LocalMap2
            key={`map-${mapKey}`}
            latitude={selectedLocation?.latitude ?? mapCenter.lat}
            longitude={selectedLocation?.longitude ?? mapCenter.lng}
            zoom={selectedLocation ? zoomLevel : Math.max(zoomLevel, 2)}
            height="100%"
            borderRadius={0}
            showMarker={Boolean(selectedLocation)}
            style={{ boxShadow: 'none' }}
          />
        </Box>

        {/* Zoom Controls */}
        <Box
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            zIndex: 1
          }}
        >
          <IconButton
            onClick={handleZoomIn}
            sx={{
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': { bgcolor: '#f5f5f5' },
              width: 40,
              height: 40
            }}
          >
            <AddIcon />
          </IconButton>

          <IconButton
            onClick={handleZoomOut}
            sx={{
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': { bgcolor: '#f5f5f5' },
              width: 40,
              height: 40
            }}
          >
            <RemoveIcon />
          </IconButton>
        </Box>

        {/* Layers Button */}
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            bottom: 16,
            bgcolor: 'rgba(0,0,0,0.7)',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            zIndex: 1,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.8)'
            }
          }}
        >
          <LayersIcon sx={{ color: 'white', fontSize: 20, mr: 1 }} />
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
            Layers
          </Typography>
        </Box>
      </Box>
      </Box>
    </Box>
  );
};

export default CountrySelector;
